import { type FC, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import {
  useAccountRecoveryV2,
  AccountVerificationType,
  AccountRecoveryStatus,
  AccountRecoveryErrorType,
} from '@egw/api'
import { Alert, AlertType, Button, InputPhone } from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { useRouter } from '@egw/router'
import { Logger } from '@egw/logger'
import { sendEvent } from '@egw/events'
import { Layout, PageHeader } from '../../../components'
import { getRecoveryFlowData, setRecoveryFlowData, updateRecoveryFlowData } from '../../../services'
import { ChannelType } from '../../../models'

const log = new Logger('Passkey Recovery - Send Phone')

interface FormData {
  phone: string | null
}

export const RecoverySendPhonePage: FC = () => {
  const { t } = useTranslation('features-auth')
  const { navigate } = useRouter()

  const { mutate, isLoading, error } = useAccountRecoveryV2()

  const {
    control,
    handleSubmit,
    formState: { errors: formErrors, isValid },
    setError,
  } = useForm({
    mode: 'all',
    defaultValues: {
      phone: null,
    },
  })

  useEffect(() => {
    // Check recovery flow data
    const data = getRecoveryFlowData()
    if (!data) {
      setRecoveryFlowData({ channel: ChannelType.PHONE, isPhoneVerified: false, isEmailVerified: false })
    }
  }, [])

  const onSubmit = handleSubmit(async (fData: unknown): Promise<void> => {
    sendEvent('user_sendVerification_click', {
      eventSource: 'egWealth',
      category: 'passkey-recovery',
      subCategory: 'verification',
      verificationType: 'phone',
    })
    const formData = fData as FormData
    if (formData.phone) {
      const phone = {
        countryPhoneCode: parseInt(formData.phone.toString().slice(1, 2), 10),
        phoneNumber: formData.phone.toString().slice(2),
      }
      updateRecoveryFlowData((data) => {
        data.phone = phone
        data.isEmailVerified = false
        data.isPhoneVerified = false
      })
      try {
        const response = await mutate({
          phone,
          sendVerificationMethod: true,
          verifyType: AccountVerificationType.PHONE_OTP,
        })
        if (
          response?.__typename === 'AccountRecoveryResponseV2' &&
          response.status === AccountRecoveryStatus.VERIFICATION_SENT
        ) {
          navigate(WEALTH_ROUTES.RECOVERY_SUBMIT.path)
        } else if (
          response?.__typename === 'AccountRecoveryError' &&
          response.errorType === AccountRecoveryErrorType.EXPIRED
        ) {
          navigate(`${WEALTH_ROUTES.SIGN_IN.path}?recoveryExpired=true`)
        } else {
          log.error('Error sending phone recovery code: ', response)
          setError('phone', { message: t('recovery.generic_error') })
        }
      } catch (e) {
        log.error('Error sending phone recovery code', e)
      }
    }
  })

  return (
    <Layout>
      <form onSubmit={(e) => void onSubmit(e)} className="flex-1 flex flex-col justify-start md:justify-center">
        <PageHeader title={t('recovery.title')} backLinkUrl={WEALTH_ROUTES.RECOVERY_SEND_PHONE.backPath} />

        <p className="mb-sm">{t('recovery.send_phone_message')}</p>

        <Controller
          name="phone"
          control={control}
          rules={{
            required: t('recovery.invalid_phone'),
            validate: (value) => {
              if (!value) {
                return t('recovery.invalid_phone')
              }
              const v = value as string
              return v.length !== 12 ? t('recovery.invalid_phone') : true
            },
          }}
          render={({ field }) => (
            <InputPhone
              {...field}
              label={t('recovery.mobile_phone')}
              className="max-w-[610px]"
              required
              disabled={isLoading}
              error={formErrors.phone?.message}
            />
          )}
        />

        <div className="flex-1 md:hidden" />

        {error ? (
          <Alert alertType={AlertType.Error} description={t('recovery.generic_error')} className="mt-sm" />
        ) : null}

        <Button
          type="submit"
          isDisabled={!isValid}
          isLoading={isLoading}
          testId="submitSendCode_primary_btn"
          className="w-full md:w-min mt-lg"
        >
          {t('recovery.send_code')}
        </Button>
      </form>
    </Layout>
  )
}
